/**
 * useBottomSheet Hook
 *
 * Manages bottom sheet state, gestures, and animations for the creature roster.
 * Supports both touch (mobile) and mouse (desktop) interactions.
 */

import { useState, useRef, useCallback, useEffect } from 'react';

export type BottomSheetMode = 'peek' | 'expanded';

export interface UseBottomSheetOptions {
  /** Height visible in peek mode (default: 120px) */
  peekHeight?: number;
  /** Maximum height when expanded (default: 70vh equivalent) */
  maxHeight?: number;
  /** Distance to drag before snap triggers (default: 50px) */
  snapThreshold?: number;
  /** Velocity to trigger quick swipe snap (default: 0.5 px/ms) */
  velocityThreshold?: number;
  /** Called when mode changes */
  onModeChange?: (mode: BottomSheetMode) => void;
}

export interface UseBottomSheetReturn {
  /** Current sheet mode */
  mode: BottomSheetMode;
  /** Whether user is currently dragging */
  isDragging: boolean;
  /** Current Y translation during drag (0 when not dragging) */
  dragOffset: number;
  /** Handlers to attach to the drag handle element */
  handlers: {
    onTouchStart: (e: React.TouchEvent) => void;
    onTouchMove: (e: React.TouchEvent) => void;
    onTouchEnd: (e: React.TouchEvent) => void;
    onMouseDown: (e: React.MouseEvent) => void;
  };
  /** Programmatically expand the sheet */
  expand: () => void;
  /** Programmatically collapse to peek mode */
  collapse: () => void;
  /** Toggle between modes */
  toggle: () => void;
}

const DEFAULT_OPTIONS: Required<Omit<UseBottomSheetOptions, 'onModeChange'>> = {
  peekHeight: 120,
  maxHeight: 500, // Will be clamped to viewport
  snapThreshold: 50,
  velocityThreshold: 0.5,
};

export function useBottomSheet(options: UseBottomSheetOptions = {}): UseBottomSheetReturn {
  const {
    peekHeight = DEFAULT_OPTIONS.peekHeight,
    snapThreshold = DEFAULT_OPTIONS.snapThreshold,
    velocityThreshold = DEFAULT_OPTIONS.velocityThreshold,
    onModeChange,
  } = options;

  const [mode, setMode] = useState<BottomSheetMode>('peek');
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);

  // Refs for tracking gesture state
  const startYRef = useRef(0);
  const lastYRef = useRef(0);
  const lastTimeRef = useRef(0);
  const velocityRef = useRef(0);
  const isMouseDraggingRef = useRef(false);

  // Handle mode change callback
  useEffect(() => {
    onModeChange?.(mode);
  }, [mode, onModeChange]);

  // Calculate snap direction based on velocity and position
  const calculateSnapDirection = useCallback((currentOffset: number): BottomSheetMode => {
    const velocity = velocityRef.current;

    // Quick swipe up (negative velocity = moving up)
    if (velocity < -velocityThreshold) {
      return 'expanded';
    }

    // Quick swipe down (positive velocity = moving down)
    if (velocity > velocityThreshold) {
      return 'peek';
    }

    // Based on position: if dragged up past threshold, expand
    // currentOffset is negative when dragging up from peek mode
    if (mode === 'peek' && currentOffset < -snapThreshold) {
      return 'expanded';
    }

    // If dragged down past threshold from expanded, collapse
    if (mode === 'expanded' && currentOffset > snapThreshold) {
      return 'peek';
    }

    // Otherwise, snap back to current mode
    return mode;
  }, [mode, snapThreshold, velocityThreshold]);

  // Touch handlers
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    startYRef.current = touch.clientY;
    lastYRef.current = touch.clientY;
    lastTimeRef.current = Date.now();
    velocityRef.current = 0;
    setIsDragging(true);
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isDragging) return;

    const touch = e.touches[0];
    const currentY = touch.clientY;
    const deltaY = currentY - startYRef.current;

    // Calculate velocity (px/ms)
    const now = Date.now();
    const timeDelta = now - lastTimeRef.current;
    if (timeDelta > 0) {
      velocityRef.current = (currentY - lastYRef.current) / timeDelta;
    }
    lastYRef.current = currentY;
    lastTimeRef.current = now;

    // Update drag offset
    setDragOffset(deltaY);

    // Prevent page scroll while dragging sheet
    e.preventDefault();
  }, [isDragging]);

  const handleTouchEnd = useCallback(() => {
    if (!isDragging) return;

    const newMode = calculateSnapDirection(dragOffset);
    setMode(newMode);
    setIsDragging(false);
    setDragOffset(0);
    velocityRef.current = 0;
  }, [isDragging, dragOffset, calculateSnapDirection]);

  // Mouse handlers (for desktop)
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    startYRef.current = e.clientY;
    lastYRef.current = e.clientY;
    lastTimeRef.current = Date.now();
    velocityRef.current = 0;
    isMouseDraggingRef.current = true;
    setIsDragging(true);
  }, []);

  // Global mouse move/up handlers (attached to window when dragging)
  useEffect(() => {
    if (!isDragging || !isMouseDraggingRef.current) return;

    const handleMouseMove = (e: MouseEvent) => {
      const currentY = e.clientY;
      const deltaY = currentY - startYRef.current;

      // Calculate velocity
      const now = Date.now();
      const timeDelta = now - lastTimeRef.current;
      if (timeDelta > 0) {
        velocityRef.current = (currentY - lastYRef.current) / timeDelta;
      }
      lastYRef.current = currentY;
      lastTimeRef.current = now;

      setDragOffset(deltaY);
    };

    const handleMouseUp = () => {
      if (!isMouseDraggingRef.current) return;

      const newMode = calculateSnapDirection(dragOffset);
      setMode(newMode);
      setIsDragging(false);
      setDragOffset(0);
      velocityRef.current = 0;
      isMouseDraggingRef.current = false;
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragOffset, calculateSnapDirection]);

  // Programmatic controls
  const expand = useCallback(() => {
    setMode('expanded');
    setDragOffset(0);
  }, []);

  const collapse = useCallback(() => {
    setMode('peek');
    setDragOffset(0);
  }, []);

  const toggle = useCallback(() => {
    setMode(prev => prev === 'peek' ? 'expanded' : 'peek');
    setDragOffset(0);
  }, []);

  return {
    mode,
    isDragging,
    dragOffset,
    handlers: {
      onTouchStart: handleTouchStart,
      onTouchMove: handleTouchMove,
      onTouchEnd: handleTouchEnd,
      onMouseDown: handleMouseDown,
    },
    expand,
    collapse,
    toggle,
  };
}

export default useBottomSheet;
